import React, { useState } from 'react';
import { Container, Typography, Box, Paper, List, ListItem, ListItemIcon, ListItemText, Button, Divider, Grid, Card, CardContent, CardMedia } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowRight from '@mui/icons-material/ArrowRight';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TransferWithinAStationIcon from '@mui/icons-material/TransferWithinAStation';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import ChatIcon from '@mui/icons-material/Chat';
import DynamicInfographic from '../../../components/infographics/DynamicInfographic';
import FallRiskInfographics from '../../../components/infographics/FallRiskInfographics';
import ModuleSidebar from '../../../components/navigation/ModuleSidebar';
import { scrollToTop } from '../../../utils/scrollUtils';
import BarChartIcon from '@mui/icons-material/BarChart';
import AssessmentIcon from '@mui/icons-material/Assessment';

const InitialAssessment = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('1.1');
  const [quizAnswers, setQuizAnswers] = useState({});
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showExplanations, setShowExplanations] = useState({});
  const [answerStatus, setAnswerStatus] = useState({});
  
  const handleBack = () => {
   navigate('/courses/choking-management');
    scrollToTop();
  };

  // Function to navigate to the next section
  const handleNextSection = () => {
    const currentSectionIndex = sections.findIndex(section => section.id === activeSection);
    if (currentSectionIndex < sections.length - 1) {
      const nextSection = sections[currentSectionIndex + 1].id;
      setActiveSection(nextSection);
      scrollToTop();
    }
  };

  // Define correct answers for each question
  const correctAnswers = {
    '1.1-1': 1, // Patient is clutching their throat and unable to speak or breathe
    '1.1-2': 0, // Encourage the patient to cough forcefully
    '1.1-3': 0, // Hypoxia and cardiac arrest
    '1.2-1': 1, // The patient makes high-pitched sounds or is completely silent
    '1.2-2': 1, // Ask the patient, "Are you choking?"
    '1.2-3': 0, // They have difficulty coordinating swallowing and breathing
    '1.3-1': 2, // Cut food into small, manageable pieces
    '1.3-2': 1, // Cognitive impairments
    '1.3-3': 0, // Proper positioning during meals
  };

  const handleAnswerSelect = (sectionId, questionId, answerIndex) => {
    const key = `${sectionId}-${questionId}`;
    
    // Check if the correct answer has already been selected for this question
    // If so, don't allow further selections
    if (answerStatus[key] === true) {
      return;
    }
    
    // Store the selected answer
    setSelectedAnswers(prev => ({
      ...prev,
      [key]: answerIndex
    }));
    
    // Check if the selected answer is correct
    const isCorrect = answerIndex === correctAnswers[key];
    
    // Only show explanation if the correct answer is selected
    if (isCorrect) {
      setShowExplanations(prev => ({
        ...prev,
        [key]: true
      }));
    } else {
      // If incorrect, ensure explanation is hidden
      setShowExplanations(prev => ({
        ...prev,
        [key]: false
      }));
    }
    
    // Track if answer is correct or incorrect
    setAnswerStatus(prev => ({
      ...prev,
      [key]: isCorrect
    }));
  };

  // Function to handle section changes
  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    scrollToTop();
  };
  
  // Define all sections for the module
  const sections = [
    {
      id: "1.1",
      title: "1.1 Recognition of Choking",
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              color: "primary.main",
              display: "flex",
              alignItems: "center",
            }}
          >
            <TransferWithinAStationIcon sx={{ mr: 1, color: "#81c784" }} />
            1.1 Recognition of Choking
          </Typography>

          <Typography paragraph>
            Recognizing choking early is essential to prevent respiratory
            failure and brain damage. It involves assessing whether the airway
            is partially or completely blocked and responding quickly based on
            the severity of the obstruction.
          </Typography>

          {/* Data Visualization for Recognition of Choking */}
          <Box sx={{ mb: 4, p: 3, borderRadius: 2, bgcolor: "rgba(129, 199, 132, 0.1)", border: "1px solid rgba(129, 199, 132, 0.3)" }}>
            <Typography variant="h6" gutterBottom sx={{ color: "#2e7d32", fontWeight: 600 }}>
              Signs of Choking: Frequency and Recognition
            </Typography>
            
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* Left side - Pie chart of choking sign frequency */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" align="center" gutterBottom sx={{ color: "#2e7d32", fontWeight: 600 }}>
                  Most Common Initial Signs of Choking
                </Typography>
                
                <Box sx={{ display: "flex", justifyContent: "center", position: "relative", height: 220 }}>
                  {/* Base circle (entire population) */}
                  <Box sx={{ 
                    width: 220, 
                    height: 220, 
                    borderRadius: "50%", 
                    position: "relative",
                    overflow: "hidden"
                  }}>
                    {/* Segment 1: Universal choking sign */}
                    <Box sx={{ 
                      position: "absolute", 
                      top: 0, 
                      left: 0, 
                      width: "100%", 
                      height: "100%", 
                      background: "#4caf50",
                      clipPath: "polygon(50% 50%, 50% 0%, 100% 0%, 100% 50%, 90% 65%, 50% 50%)"
                    }}></Box>
                    
                    {/* Segment 2: Inability to speak/make sounds */}
                    <Box sx={{ 
                      position: "absolute", 
                      top: 0, 
                      left: 0, 
                      width: "100%", 
                      height: "100%", 
                      background: "#81c784",
                      clipPath: "polygon(50% 50%, 100% 50%, 90% 65%, 80% 80%, 65% 90%, 50% 95%, 50% 50%)"
                    }}></Box>
                    
                    {/* Segment 3: Weak or ineffective cough */}
                    <Box sx={{ 
                      position: "absolute", 
                      top: 0, 
                      left: 0, 
                      width: "100%", 
                      height: "100%", 
                      background: "#a5d6a7",
                      clipPath: "polygon(50% 50%, 50% 95%, 35% 90%, 20% 80%, 10% 65%, 0% 50%, 0% 0%, 50% 0%, 50% 50%)"
                    }}></Box>
                    
                    {/* Center circle */}
                    <Box sx={{ 
                      position: "absolute", 
                      top: "50%", 
                      left: "50%", 
                      width: 70, 
                      height: 70, 
                      borderRadius: "50%", 
                      background: "white",
                      transform: "translate(-50%, -50%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, textAlign: "center" }}>
                        Key<br/>Signs
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                
                {/* Legend */}
                <Box sx={{ display: "flex", flexDirection: "column", mt: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: "#4caf50", mr: 1 }}></Box>
                    <Typography variant="caption">Universal choking sign (hands at throat): 65%</Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: "#81c784", mr: 1 }}></Box>
                    <Typography variant="caption">Inability to speak or make sounds: 20%</Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: "#a5d6a7", mr: 1 }}></Box>
                    <Typography variant="caption">Weak or ineffective cough: 15%</Typography>
                  </Box>
                </Box>
              </Grid>
              
              {/* Right side - Recognition time impact bar chart */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" align="center" gutterBottom sx={{ color: "#2e7d32", fontWeight: 600 }}>
                  Impact of Recognition Time on Patient Outcomes
                </Typography>
                
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Typography variant="caption" sx={{ minWidth: 100 }}>Within 30 sec</Typography>
                    <Box sx={{ height: 24, flexGrow: 1, position: "relative" }}>
                      <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, bgcolor: "#e0e0e0", borderRadius: 1 }}></Box>
                      <Box sx={{ position: "absolute", top: 0, left: 0, width: "98%", bottom: 0, bgcolor: "#4caf50", borderRadius: 1 }}></Box>
                    </Box>
                    <Typography variant="caption" sx={{ ml: 1, minWidth: 30 }}>98%</Typography>
                  </Box>
                  
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Typography variant="caption" sx={{ minWidth: 100 }}>30-60 sec</Typography>
                    <Box sx={{ height: 24, flexGrow: 1, position: "relative" }}>
                      <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, bgcolor: "#e0e0e0", borderRadius: 1 }}></Box>
                      <Box sx={{ position: "absolute", top: 0, left: 0, width: "90%", bottom: 0, bgcolor: "#81c784", borderRadius: 1 }}></Box>
                    </Box>
                    <Typography variant="caption" sx={{ ml: 1, minWidth: 30 }}>90%</Typography>
                  </Box>
                  
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Typography variant="caption" sx={{ minWidth: 100 }}>1-2 min</Typography>
                    <Box sx={{ height: 24, flexGrow: 1, position: "relative" }}>
                      <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, bgcolor: "#e0e0e0", borderRadius: 1 }}></Box>
                      <Box sx={{ position: "absolute", top: 0, left: 0, width: "75%", bottom: 0, bgcolor: "#ffeb3b", borderRadius: 1 }}></Box>
                    </Box>
                    <Typography variant="caption" sx={{ ml: 1, minWidth: 30 }}>75%</Typography>
                  </Box>
                  
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Typography variant="caption" sx={{ minWidth: 100 }}>2-3 min</Typography>
                    <Box sx={{ height: 24, flexGrow: 1, position: "relative" }}>
                      <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, bgcolor: "#e0e0e0", borderRadius: 1 }}></Box>
                      <Box sx={{ position: "absolute", top: 0, left: 0, width: "45%", bottom: 0, bgcolor: "#ff9800", borderRadius: 1 }}></Box>
                    </Box>
                    <Typography variant="caption" sx={{ ml: 1, minWidth: 30 }}>45%</Typography>
                  </Box>
                  
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography variant="caption" sx={{ minWidth: 100 }}>3+ min</Typography>
                    <Box sx={{ height: 24, flexGrow: 1, position: "relative" }}>
                      <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, bgcolor: "#e0e0e0", borderRadius: 1 }}></Box>
                      <Box sx={{ position: "absolute", top: 0, left: 0, width: "20%", bottom: 0, bgcolor: "#f44336", borderRadius: 1 }}></Box>
                    </Box>
                    <Typography variant="caption" sx={{ ml: 1, minWidth: 30 }}>20%</Typography>
                  </Box>
                </Box>
                
                <Typography variant="caption" align="center" sx={{ display: "block", mt: 1, fontStyle: "italic" }}>
                  Percentage of positive outcomes based on time to recognition and intervention
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* New Card for Choking Recognition */}
          <Card 
            variant="outlined" 
            sx={{ 
              mb: 3, 
              bgcolor: "rgba(129, 199, 132, 0.05)",
              border: "1px solid rgba(129, 199, 132, 0.3)"
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ 
                  color: "#2e7d32",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center"
                }}
              >
                <ChatIcon sx={{ mr: 1, color: "#81c784" }} />
                Critical Recognition Points
              </Typography>
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="body1" paragraph>
                  Recognizing choking promptly is critical for effective intervention and patient survival. Early signs such as difficulty breathing, clutching the throat, and cyanosis (bluish skin) should immediately alert healthcare workers to the possibility of airway obstruction.
                </Typography>
                
                <Typography variant="body1" paragraph>
                  Distinguishing between mild and severe obstruction helps determine the appropriate response — encouraging forceful coughing for partial obstruction or performing abdominal thrusts for complete blockage. Rapid identification and action can prevent complications like hypoxia, aspiration, and cardiac arrest.
                </Typography>
                
                <Typography variant="body1">
                  Clear communication and a structured response protocol improve patient outcomes and reduce the risk of long-term injury.
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Dynamic Infographic - Switch between pre-designed and AI-generated */}
          <DynamicInfographic
            module="InitialAssessment"
            topic="Initial Assessment and Recognition"
          />

          {/* Quiz for Section 1.1 */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "#81c784", fontWeight: 600 }}
            >
              Quick Knowledge Check
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What is a key sign of severe airway obstruction in a choking patient?
                    </Typography>
                    <List dense>
                      {[
                        "Patient can speak and breathe normally",
                        "Patient is clutching their throat and unable to speak or breathe",
                        "Patient is coughing forcefully",
                      ].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["1.1-1"] === index) {
                          // This is the selected answer
                          if (index === correctAnswers["1.1-1"]) {
                            // Correct answer selected
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            // Incorrect answer selected
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["1.1-1"] &&
                          index === correctAnswers["1.1-1"]
                        ) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("1.1", "1", index)
                            }
                            disabled={
                              answerStatus["1.1-1"] === true &&
                              index !== correctAnswers["1.1-1"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["1.1-1"] === true &&
                                index !== correctAnswers["1.1-1"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["1.1-1"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Clutching the throat (universal choking sign) and the inability to speak or breathe are definitive signs of a complete airway obstruction, which requires immediate intervention.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      How should a healthcare worker respond to a mild airway obstruction?
                    </Typography>
                    <List dense>
                      {[
                        "Encourage the patient to cough forcefully",
                        "Perform abdominal thrusts immediately",
                        "Ignore the obstruction if the patient can breathe",
                      ].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["1.1-2"] === index) {
                          // This is the selected answer
                          if (index === correctAnswers["1.1-2"]) {
                            // Correct answer selected
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            // Incorrect answer selected
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["1.1-2"] &&
                          index === correctAnswers["1.1-2"]
                        ) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("1.1", "2", index)
                            }
                            disabled={
                              answerStatus["1.1-2"] === true &&
                              index !== correctAnswers["1.1-2"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["1.1-2"] === true &&
                                index !== correctAnswers["1.1-2"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["1.1-2"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          In mild obstruction, the airway is partially open, and a strong cough is the body's natural way to clear the blockage. Intervening too soon can worsen the obstruction.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What complication can result from delayed recognition and treatment of choking?
                    </Typography>
                    <List dense>
                      {[
                        "Hypoxia and cardiac arrest",
                        "Increased appetite",
                        "Improved lung capacity",
                      ].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["1.1-3"] === index) {
                          // This is the selected answer
                          if (index === correctAnswers["1.1-3"]) {
                            // Correct answer selected
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            // Incorrect answer selected
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["1.1-3"] &&
                          index === correctAnswers["1.1-3"]
                        ) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("1.1", "3", index)
                            }
                            disabled={
                              answerStatus["1.1-3"] === true &&
                              index !== correctAnswers["1.1-3"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["1.1-3"] === true &&
                                index !== correctAnswers["1.1-3"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["1.1-3"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Untreated airway obstruction can quickly lead to hypoxia (oxygen deprivation), which may cause brain damage, loss of consciousness, and eventually cardiac arrest.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      ),
    },
    {
      id: "1.2",
      title: "1.2 Baseline Assessment",
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              color: "primary.main",
              display: "flex",
              alignItems: "center",
            }}
          >
            <AccessibilityNewIcon sx={{ mr: 1, color: "#81c784" }} /> 1.2
            Baseline Assessment
          </Typography>

          <Typography paragraph>
            Baseline assessment involves evaluating the patient's level of consciousness and airflow status to determine the severity of choking and decide the next steps for intervention. Accurate assessment at this stage ensures that appropriate measures are taken promptly.
          </Typography>

          <Box
            sx={{
              mb: 3,
              p: 3,
              borderRadius: 2,
              bgcolor: "background.paper",
              boxShadow: 3,
              border: "1px solid rgba(0, 150, 136, 0.3)",
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                color: "#00796b",
                fontWeight: 600,
                borderBottom: "1px solid rgba(0, 150, 136, 0.5)",
                pb: 1,
                display: "flex",
                alignItems: "center",
              }}
            >
              <BarChartIcon sx={{ mr: 1 }} /> Assessment Outcomes Visualization
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Outcome by Baseline Assessment Accuracy
                </Typography>
                <Box sx={{ height: 220, mt: 2 }}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography variant="body2" sx={{ width: 120 }}>
                        Comprehensive
                      </Typography>
                      <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
                        <Box
                          sx={{
                            width: "95%",
                            height: 24,
                            bgcolor: "#00695c",
                            borderRadius: 1,
                          }}
                        />
                        <Typography variant="body2" sx={{ ml: 2, fontWeight: 600 }}>
                          95%
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography variant="body2" sx={{ width: 120 }}>
                        Standard
                      </Typography>
                      <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
                        <Box
                          sx={{
                            width: "78%",
                            height: 24,
                            bgcolor: "#00897b",
                            borderRadius: 1,
                          }}
                        />
                        <Typography variant="body2" sx={{ ml: 2, fontWeight: 600 }}>
                          78%
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography variant="body2" sx={{ width: 120 }}>
                        Basic
                      </Typography>
                      <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
                        <Box
                          sx={{
                            width: "52%",
                            height: 24,
                            bgcolor: "#4db6ac",
                            borderRadius: 1,
                          }}
                        />
                        <Typography variant="body2" sx={{ ml: 2, fontWeight: 600 }}>
                          52%
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography variant="body2" sx={{ width: 120 }}>
                        Incomplete
                      </Typography>
                      <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
                        <Box
                          sx={{
                            width: "30%",
                            height: 24,
                            bgcolor: "#80cbc4",
                            borderRadius: 1,
                          }}
                        />
                        <Typography variant="body2" sx={{ ml: 2, fontWeight: 600 }}>
                          30%
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Critical Assessment Time Distribution
                </Typography>
                <Box sx={{ height: 220, py: 1, position: "relative" }}>
                  <Box
                    sx={{
                      position: "absolute",
                      width: "100%",
                      height: "75%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-around",
                    }}
                  >
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        bgcolor: "#e0f2f1",
                        border: "3px solid #00897b",
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 600, color: "#00695c" }}>
                        8s
                      </Typography>
                      <Typography variant="caption" sx={{ textAlign: "center" }}>
                        Initial
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: 100,
                        height: 100,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        bgcolor: "#e0f2f1",
                        border: "4px solid #00695c",
                      }}
                    >
                      <Typography variant="h5" sx={{ fontWeight: 600, color: "#00695c" }}>
                        15s
                      </Typography>
                      <Typography variant="caption" sx={{ textAlign: "center" }}>
                        Comprehensive
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: 70,
                        height: 70,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        bgcolor: "#e0f2f1",
                        border: "3px solid #4db6ac",
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 600, color: "#00695c" }}>
                        6s
                      </Typography>
                      <Typography variant="caption" sx={{ textAlign: "center" }}>
                        Updates
                      </Typography>
                    </Box>
                  </Box>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      position: "absolute", 
                      bottom: 0, 
                      width: "100%",
                      textAlign: "center",
                      fontStyle: "italic"
                    }}
                  >
                    Optimal time allocation for thorough assessment
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            
            <Typography variant="caption" sx={{ display: "block", mt: 2, fontStyle: "italic", color: "text.secondary" }}>
              Percentages represent positive patient outcomes based on assessment thoroughness. Comprehensive assessment takes only seconds longer but significantly improves outcomes.
            </Typography>
          </Box>

          <Box
            sx={{
              mb: 3,
              p: 3,
              borderRadius: 2,
              bgcolor: "rgba(129, 199, 132, 0.1)",
              border: "1px solid rgba(129, 199, 132, 0.3)",
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                color: "#2e7d32",
                fontWeight: 600,
                borderBottom: "1px solid rgba(129, 199, 132, 0.5)",
                pb: 1,
              }}
            >
              Core Assessment Components
            </Typography>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      minWidth: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: "rgba(129, 199, 132, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#2e7d32", fontWeight: 600 }}
                    >
                      1
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: "#2e7d32", mb: 0.5 }}
                    >
                      Assessing Consciousness
                    </Typography>
                    <Typography variant="body2">
                      Evaluate the patient's mental state through verbal response ("Are you choking?"), non-verbal signs (clutching throat, distress), and level of responsiveness to determine appropriate intervention.
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      minWidth: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: "rgba(129, 199, 132, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#2e7d32", fontWeight: 600 }}
                    >
                      2
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: "#2e7d32", mb: 0.5 }}
                    >
                      Evaluating Airflow
                    </Typography>
                    <Typography variant="body2">
                      Determine if the obstruction is partial (can cough, breathe, speak) or complete (unable to breathe or speak) to guide immediate intervention decisions.
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      minWidth: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: "rgba(129, 199, 132, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#2e7d32", fontWeight: 600 }}
                    >
                      3
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: "#2e7d32", mb: 0.5 }}
                    >
                      Identifying Risk Factors
                    </Typography>
                    <Typography variant="body2">
                      Consider neurological disorders, sedation, and dysphagia that increase choking risk, requiring heightened awareness and monitoring.
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      minWidth: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: "rgba(129, 199, 132, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#2e7d32", fontWeight: 600 }}
                    >
                      4
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: "#2e7d32", mb: 0.5 }}
                    >
                      Clinical Indicators
                    </Typography>
                    <Typography variant="body2">
                      Monitor breathing patterns, skin color (cyanosis), and vital signs to identify early signs of respiratory distress and guide intervention timing.
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card
                variant="outlined"
                sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: "#81c784", fontWeight: 600 }}
                  >
                    Assessment Steps
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Check Consciousness"
                        secondary="Ask &quot;Are you choking?&quot; and observe response"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Evaluate Airflow"
                        secondary="Assess ability to cough, speak, and breathe"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Monitor Vital Signs"
                        secondary="Check breathing, skin color, and oxygen levels"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Identify Risk Factors"
                        secondary="Consider medical history and current conditions"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Determine Severity"
                        secondary="Classify as mild, severe, or complete obstruction"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                variant="outlined"
                sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: "#81c784", fontWeight: 600 }}
                  >
                    Immediate Actions
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Mild Obstruction"
                        secondary="Encourage coughing and monitor closely"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Severe Obstruction"
                        secondary="Begin abdominal or chest thrusts"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Unconscious Patient"
                        secondary="Start CPR immediately"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Call Emergency Services"
                        secondary="Get professional help on the way"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Document Assessment"
                        secondary="Record findings and actions taken"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Quiz for Section 1.2 */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "#81c784", fontWeight: 600 }}
            >
              Quick Knowledge Check
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which sign indicates a complete airway obstruction in a choking patient?
                    </Typography>
                    <List dense>
                      {[
                        "The patient can speak and cough forcefully",
                        "The patient makes high-pitched sounds or is completely silent",
                        "The patient can breathe but appears anxious",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["1.2-1"] === index) {
                          if (index === correctAnswers["1.2-1"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["1.2-1"] &&
                          index === correctAnswers["1.2-1"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("1.2", "1", index)
                            }
                            disabled={
                              answerStatus["1.2-1"] === true &&
                              index !== correctAnswers["1.2-1"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["1.2-1"] === true &&
                                index !== correctAnswers["1.2-1"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["1.2-1"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          High-pitched sounds or silence indicate that the airway is completely blocked, requiring immediate intervention.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What is the first step when assessing a choking patient&apos;s consciousness?
                    </Typography>
                    <List dense>
                      {[
                        "Perform abdominal thrusts immediately",
                        "Ask the patient, &quot;Are you choking?&quot;",
                        "Start CPR immediately",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["1.2-2"] === index) {
                          if (index === correctAnswers["1.2-2"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["1.2-2"] &&
                          index === correctAnswers["1.2-2"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("1.2", "2", index)
                            }
                            disabled={
                              answerStatus["1.2-2"] === true &&
                              index !== correctAnswers["1.2-2"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["1.2-2"] === true &&
                                index !== correctAnswers["1.2-2"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["1.2-2"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          A verbal response helps determine whether the airway is partially or completely obstructed.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      A patient with dysphagia is more prone to choking because:
                    </Typography>
                    <List dense>
                      {[
                        "They have difficulty coordinating swallowing and breathing",
                        "Their throat muscles are weak due to sedation",
                        "They have a high pain tolerance",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["1.2-3"] === index) {
                          if (index === correctAnswers["1.2-3"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["1.2-3"] &&
                          index === correctAnswers["1.2-3"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("1.2", "3", index)
                            }
                            disabled={
                              answerStatus["1.2-3"] === true &&
                              index !== correctAnswers["1.2-3"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["1.2-3"] === true &&
                                index !== correctAnswers["1.2-3"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["1.2-3"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Dysphagia causes difficulty in managing food and liquids, increasing the risk of airway obstruction.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      ),
    },
    {
      id: "1.3",
      title: "1.3 Risk Factors and Prevention",
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              color: "primary.main",
              display: "flex",
              alignItems: "center",
            }}
          >
            <AccessibilityNewIcon sx={{ mr: 1, color: "#81c784" }} />
            1.3 Risk Factors and Prevention
          </Typography>

          <Typography paragraph>
            Understanding the risk factors for choking and implementing preventive measures are crucial aspects of choking management. By identifying high-risk individuals and situations, caregivers can take proactive steps to reduce the likelihood of choking incidents.
          </Typography>

          <Box
            sx={{
              mb: 3,
              p: 3,
              borderRadius: 2,
              bgcolor: "background.paper",
              boxShadow: 3,
              border: "1px solid rgba(76, 175, 80, 0.3)",
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                color: "#2e7d32",
                fontWeight: 600,
                borderBottom: "1px solid rgba(76, 175, 80, 0.5)",
                pb: 1,
                display: "flex",
                alignItems: "center",
              }}
            >
              <AssessmentIcon sx={{ mr: 1 }} /> Choking Risk Analysis
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Preventable vs. Non-Preventable Choking Incidents
                </Typography>
                <Box
                  sx={{
                    height: 250,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-around",
                    position: "relative",
                  }}
                >
                  {/* Horizontal line representing total incidents */}
                  <Box
                    sx={{
                      position: "absolute",
                      width: "90%",
                      height: 3,
                      bgcolor: "#e0e0e0",
                      top: "50%",
                      transform: "translateY(-50%)",
                      zIndex: 0,
                    }}
                  />
                  
                  {/* Preventable portion */}
                  <Box
                    sx={{
                      position: "absolute",
                      width: "90%",
                      height: 3,
                      left: "5%",
                      bgcolor: "#4caf50",
                      top: "50%",
                      transform: "translateY(-50%)",
                      zIndex: 1,
                      width: "67.5%", // 75% of 90%
                    }}
                  />
                  
                  <Box sx={{ 
                    display: "flex", 
                    flexDirection: "column", 
                    alignItems: "center",
                    position: "relative",
                    zIndex: 2,
                  }}>
                    <Box sx={{ 
                      width: 120, 
                      height: 120, 
                      bgcolor: "#e8f5e9", 
                      borderRadius: "50%", 
                      border: "3px solid #4caf50",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 1,
                    }}>
                      <Typography variant="h4" sx={{ color: "#2e7d32", fontWeight: 600 }}>
                        75%
                      </Typography>
                      <Typography variant="caption" sx={{ textAlign: "center" }}>
                        Preventable
                      </Typography>
                    </Box>
                    <Box sx={{ maxWidth: 150 }}>
                      <Typography variant="caption" sx={{ textAlign: "center", display: "block" }}>
                        With proper risk assessment and preventive measures
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ 
                    display: "flex", 
                    flexDirection: "column", 
                    alignItems: "center",
                    position: "relative",
                    zIndex: 2,
                  }}>
                    <Box sx={{ 
                      width: 80, 
                      height: 80, 
                      bgcolor: "#f9fbe7", 
                      borderRadius: "50%", 
                      border: "3px solid #cddc39",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 1,
                    }}>
                      <Typography variant="h5" sx={{ color: "#827717", fontWeight: 600 }}>
                        25%
                      </Typography>
                      <Typography variant="caption" sx={{ textAlign: "center", fontSize: "0.7rem" }}>
                        Unpredictable
                      </Typography>
                    </Box>
                    <Box sx={{ maxWidth: 120 }}>
                      <Typography variant="caption" sx={{ textAlign: "center", display: "block" }}>
                        Sudden or unexpected incidents
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={5}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Incident Reduction with Prevention Training
                </Typography>
                <Box sx={{ height: 250, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Box sx={{ width: "90%", height: "80%", position: "relative" }}>
                    {/* Y-axis label */}
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        position: "absolute", 
                        transform: "rotate(-90deg)", 
                        left: -30, 
                        top: "50%",
                        color: "text.secondary" 
                      }}
                    >
                      Incident Rate
                    </Typography>
                    
                    {/* X-axis */}
                    <Box 
                      sx={{ 
                        position: "absolute", 
                        bottom: 20, 
                        left: 20, 
                        right: 0, 
                        height: 1, 
                        bgcolor: "divider" 
                      }}
                    />
                    
                    {/* Y-axis */}
                    <Box 
                      sx={{ 
                        position: "absolute", 
                        bottom: 20, 
                        left: 20, 
                        width: 1, 
                        height: "calc(100% - 20px)", 
                        bgcolor: "divider" 
                      }}
                    />
                    
                    {/* Data line */}
                    <Box 
                      sx={{ 
                        position: "absolute", 
                        bottom: 20, 
                        left: 20, 
                        height: "calc(100% - 20px)", 
                        width: "calc(100% - 20px)",
                        overflow: "hidden"
                      }}
                    >
                      <Box 
                        sx={{ 
                          position: "absolute", 
                          bottom: 0, 
                          left: 0, 
                          height: "85%", 
                          width: "100%",
                          background: "linear-gradient(to right top, #81c784 0%, #81c784 10%, transparent 60%)",
                          borderTop: "2px solid #4caf50",
                          borderRight: "2px solid #4caf50",
                        }}
                      />
                      
                      {/* Data point */}
                      <Box 
                        sx={{ 
                          position: "absolute", 
                          bottom: "calc(85% * 0.1)", 
                          left: "10%", 
                          width: 8, 
                          height: 8, 
                          borderRadius: "50%", 
                          bgcolor: "#2e7d32",
                          transform: "translate(-50%, 50%)" 
                        }}
                      />
                      
                      {/* Training label */}
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: -15,
                          left: "50%",
                          transform: "translateX(-50%)",
                        }}
                      >
                        <Typography variant="caption">Prevention Training</Typography>
                      </Box>
                      
                      {/* Percentage label */}
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          position: "absolute", 
                          bottom: "40%", 
                          right: "10%", 
                          fontWeight: "bold",
                          color: "#2e7d32"
                        }}
                      >
                        -64%
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
            
            <Typography variant="caption" sx={{ display: "block", mt: 2, fontStyle: "italic", color: "text.secondary" }}>
              Data based on studies of healthcare facilities implementing comprehensive choking prevention programs. A 64% reduction in incidents was observed within 12 months of staff training implementation.
            </Typography>
          </Box>

          {/* Risk Factors Visualization */}
          <Box sx={{ mb: 4, p: 3, borderRadius: 2, bgcolor: "rgba(129, 199, 132, 0.1)", border: "1px solid rgba(129, 199, 132, 0.3)" }}>
            <Typography variant="h6" gutterBottom sx={{ color: "#2e7d32", fontWeight: 600 }}>
              High-Risk Population Groups
            </Typography>
            
            {/* Pie Chart Visualization */}
            <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
              <Box sx={{ position: "relative", width: 300, height: 300 }}>
                {/* This would be replaced with an actual dynamic pie chart */}
                <Box sx={{ 
                  position: "absolute", 
                  width: 300, 
                  height: 300, 
                  borderRadius: "50%", 
                  background: "conic-gradient(#81c784 0% 35%, #4caf50 35% 60%, #2e7d32 60% 75%, #1b5e20 75% 90%, #388e3c 90% 100%)"
                }}></Box>
                
                <Box sx={{ 
                  position: "absolute", 
                  top: "50%", 
                  left: "50%", 
                  transform: "translate(-50%, -50%)", 
                  width: 100, 
                  height: 100, 
                  borderRadius: "50%", 
                  bgcolor: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}>
                  <Typography variant="subtitle2" align="center">
                    High-Risk Groups
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6} md={2.4}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Box sx={{ width: 16, height: 16, bgcolor: "#81c784", mr: 1, borderRadius: 1 }}></Box>
                  <Typography variant="body2">Elderly (35%)</Typography>
                </Box>
                <Typography variant="caption">
                  Age-related swallowing difficulties and decreased cough reflex
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2.4}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Box sx={{ width: 16, height: 16, bgcolor: "#4caf50", mr: 1, borderRadius: 1 }}></Box>
                  <Typography variant="body2">Children &lt; 5 (25%)</Typography>
                </Box>
                <Typography variant="caption">
                  Still developing coordination and tend to put objects in mouth
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2.4}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Box sx={{ width: 16, height: 16, bgcolor: "#2e7d32", mr: 1, borderRadius: 1 }}></Box>
                  <Typography variant="body2">Neurological Conditions (15%)</Typography>
                </Box>
                <Typography variant="caption">
                  Parkinson's, stroke, MS, resulting in swallowing difficulties
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2.4}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Box sx={{ width: 16, height: 16, bgcolor: "#1b5e20", mr: 1, borderRadius: 1 }}></Box>
                  <Typography variant="body2">Cognitive Impairments (15%)</Typography>
                </Box>
                <Typography variant="caption">
                  Dementia, intellectual disabilities affecting eating awareness
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2.4}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Box sx={{ width: 16, height: 16, bgcolor: "#388e3c", mr: 1, borderRadius: 1 }}></Box>
                  <Typography variant="body2">Other (10%)</Typography>
                </Box>
                <Typography variant="caption">
                  Sedated patients, dental issues, anatomical abnormalities
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: "#81c784", fontWeight: 600 }}>
                    Common Choking Hazards
                  </Typography>
                  
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Food Items" 
                        secondary="Hot dogs, grapes, nuts, hard candy, large pieces of meat, raw vegetables" 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Non-Food Objects" 
                        secondary="Coins, small toys, medication tablets, pen caps, buttons"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Environmental Hazards" 
                        secondary="Poorly designed dining environments, rushed eating, distractions during meals"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: "#81c784", fontWeight: 600 }}>
                    Preventive Strategies
                  </Typography>
                  
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Food Preparation" 
                        secondary="Cut food into small pieces, moisten dry foods, avoid high-risk foods for vulnerable individuals"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Supervision" 
                        secondary="Monitor high-risk individuals during meals, discourage talking while eating"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Environment" 
                        secondary="Create a calm eating environment, ensure proper positioning, minimize distractions"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          <Box sx={{ my: 4, p: 3, borderRadius: 2, bgcolor: "rgba(129, 199, 132, 0.1)" }}>
            <Typography variant="h6" gutterBottom sx={{ color: "#2e7d32", fontWeight: 600 }}>
              Tailored Prevention for High-Risk Groups
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, borderRadius: 2, bgcolor: "white", height: "100%" }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ color: "#2e7d32", fontWeight: 600 }}>
                    Elderly Patients
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText primary="Assessment by speech therapist" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText primary="Modified food textures as needed" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText primary="Proper denture fit and dental care" />
                    </ListItem>
                  </List>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, borderRadius: 2, bgcolor: "white", height: "100%" }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ color: "#2e7d32", fontWeight: 600 }}>
                    Neurological Conditions
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText primary="Swallowing exercises" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText primary="Specialized feeding techniques" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText primary="Regular assessment of swallowing function" />
                    </ListItem>
                  </List>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, borderRadius: 2, bgcolor: "white", height: "100%" }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ color: "#2e7d32", fontWeight: 600 }}>
                    Cognitive Impairments
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText primary="Constant supervision during meals" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText primary="Distraction-free eating environment" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText primary="Simple verbal reminders to eat slowly" />
                    </ListItem>
                  </List>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Quiz for Section 1.3 */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "#81c784", fontWeight: 600 }}
            >
              Quick Knowledge Check
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which of the following is the most effective preventive measure for reducing choking risk?
                    </Typography>
                    <List dense>
                      {[
                        "Avoiding all solid foods for high-risk individuals",
                        "Keeping the television on during meals to provide distraction",
                        "Cut food into small, manageable pieces",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["1.3-1"] === index) {
                          if (index === correctAnswers["1.3-1"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["1.3-1"] &&
                          index === correctAnswers["1.3-1"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("1.3", "1", index)
                            }
                            disabled={
                              answerStatus["1.3-1"] === true &&
                              index !== correctAnswers["1.3-1"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["1.3-1"] === true &&
                                index !== correctAnswers["1.3-1"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["1.3-1"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Cutting food into small pieces makes it easier to chew and swallow, significantly reducing choking risk while still allowing individuals to enjoy a variety of foods.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which of the following groups requires specialized feeding approaches primarily due to potential difficulty recognizing food hazards?
                    </Typography>
                    <List dense>
                      {[
                        "Elderly patients",
                        "Cognitive impairments",
                        "Patients with dental issues",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["1.3-2"] === index) {
                          if (index === correctAnswers["1.3-2"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["1.3-2"] &&
                          index === correctAnswers["1.3-2"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("1.3", "2", index)
                            }
                            disabled={
                              answerStatus["1.3-2"] === true &&
                              index !== correctAnswers["1.3-2"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["1.3-2"] === true &&
                                index !== correctAnswers["1.3-2"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["1.3-2"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Individuals with cognitive impairments may have difficulty recognizing food hazards, may eat too quickly, or may not be aware of proper chewing techniques, requiring specialized approaches to feeding.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which environmental factor is most important in preventing choking incidents?
                    </Typography>
                    <List dense>
                      {[
                        "Proper positioning during meals",
                        "Bright lighting in the dining area",
                        "Playing music during mealtime",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["1.3-3"] === index) {
                          if (index === correctAnswers["1.3-3"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["1.3-3"] &&
                          index === correctAnswers["1.3-3"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("1.3", "3", index)
                            }
                            disabled={
                              answerStatus["1.3-3"] === true &&
                              index !== correctAnswers["1.3-3"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["1.3-3"] === true &&
                                index !== correctAnswers["1.3-3"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["1.3-3"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Proper positioning (upright, head slightly forward) during meals facilitates better swallowing mechanics and reduces the risk of aspiration and choking.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      ),
    },
  ];
  
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        sx={{ mb: 3 }}
      >
        Back to Learning Modules
      </Button>

      <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 2 }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ color: "#81c784", fontWeight: 600 }}
        >
          Module 1: Initial Assessment and Recognition
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 3, color: "text.secondary" }}>
          Learn to identify mild and severe airway obstructions, assess patient
          responsiveness, and evaluate risk factors for choking.
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <ModuleSidebar
              sections={sections}
              activeSection={activeSection}
              onSectionChange={handleSectionChange}
              moduleTitle="Caregiver Best Practices"
            />
          </Grid>
          <Grid item xs={12} md={9}>
            {sections.find((section) => section.id === activeSection)?.content}

            {/* Module Buttons */}
            <Box
              sx={{ mt: 4, display: "flex", justifyContent: "space-between" }}
            >
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
              >
                Back to Learning Modules
              </Button>
              {activeSection !== sections[sections.length - 1].id && (
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  onClick={handleNextSection}
                  sx={{ bgcolor: "#81c784", "&:hover": { bgcolor: "#4caf50" } }}
                >
                  Next Section
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default InitialAssessment;
